import React, { useState } from 'react'
import Button from 'uiComponents/buttons'
import { DisabledItemHoverInfotip } from 'uiComponents/infotip'
import { Modal, ModalBody, ModalFooter } from 'uiComponents/popups/modal.new'
import { LucideIcon } from 'utils/lucideIcon'
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard'
import { ServiceAppWithSecret } from '../../schema'
import './secretRevealModal.scss'

interface SecretRevealModalProps {
    app: ServiceAppWithSecret
    reason: 'created' | 'rotated'
    onDone: () => void
}

const CopyableValue: React.FC<{ label: string; value: string; secret?: boolean }> = ({ label, value, secret }) => {
    const { copied, copy } = useCopyToClipboard()

    return (
        <div className="service-app-secret-field">
            <span className="service-app-secret-field-label">{label}</span>
            <div className="service-app-secret-field-row">
                <code className={secret ? 'service-app-secret-value is-secret' : 'service-app-secret-value'}>
                    {value}
                </code>
                <button type="button" className="service-app-copy-button" onClick={() => copy(value)}>
                    <LucideIcon iconName={copied ? 'Check' : 'Copy'} size={14} />
                    {copied ? 'Copied' : 'Copy'}
                </button>
            </div>
        </div>
    )
}

export const SecretRevealModal: React.FC<SecretRevealModalProps> = ({ app, reason, onDone }) => {
    const [acknowledged, setAcknowledged] = useState(false)

    return (
        <Modal className="service-app-secret-modal">
            <ModalBody>
                <span className="service-app-secret-eyebrow">
                    <LucideIcon iconName="ShieldCheck" size={14} />
                    One-time secret
                </span>
                <h4 className="service-app-secret-heading">
                    {reason === 'created' ? 'Your service app is ready.' : 'Your new secret is ready.'}
                </h4>
                <p className="service-app-secret-intro">
                    Copy the secret now — you won&apos;t see it again. Keep it in a secrets manager or environment
                    variable, never in your code.
                    {reason === 'rotated' && ' The old secret has already stopped working.'}
                </p>

                <CopyableValue label="Client ID" value={app.clientId} />
                <CopyableValue label="Client secret" value={app.clientSecret} secret />

                <div className="service-app-secret-warning">
                    <LucideIcon iconName="TriangleAlert" size={16} />
                    <span>
                        We keep a scrambled copy we can check against, but we cannot read it back to you. If you lose
                        it, create a new secret for this app.
                    </span>
                </div>

                <label className="service-app-secret-confirm">
                    <input
                        type="checkbox"
                        checked={acknowledged}
                        onChange={(e) => setAcknowledged(e.target.checked)}
                    />
                    <span>I stored the secret somewhere safe</span>
                </label>
            </ModalBody>
            <ModalFooter>
                <DisabledItemHoverInfotip
                    active={!acknowledged}
                    infotipText="Copy the secret first, then tick the box above. You will not be able to see it again."
                    width="18rem"
                    toTheLeft
                >
                    <Button kind="action" disabled={!acknowledged} onClick={onDone}>
                        Back to service apps
                    </Button>
                </DisabledItemHoverInfotip>
            </ModalFooter>
        </Modal>
    )
}
